import { PropsWithChildren, useEffect } from "react";
import { useSelector } from "react-redux";
import InlineLoader from "../../shared/components/inlineLoader/InlineLoader";
import { useLocalization } from "../hooks/useLocalization";
import useMetaDataInitialization from "../hooks/useMetaDataInitialization";
import useReportMode from "../hooks/useReportMode";
import { useAppDispatch } from "../store/hooks/useAppDispatch";
import {
  selectClientSettingsLoaded,
  selectClientSettingsLoadingFailed,
} from "../store/selectors/clientSettingsSelectors";
import { selectMetaDataLoaded, selectMetaDataLoadingFailed } from "../store/selectors/metaDataSelectors";
import { loadClientSettings } from "../store/thunks/clientSettingsThunks";
import ImportantDataLoadingFailed from "./common/ImportantDataLoadingFailed";

interface Props {
  clientCode: string | undefined;
}
export const AppInitializer = ({ children, clientCode }: PropsWithChildren<Props>) => {
  const { metadata: metadataLocale, common: commonLocale } = useLocalization();
  const metaDataLoaded = useSelector(selectMetaDataLoaded);
  const metaDataLoadingFailed = useSelector(selectMetaDataLoadingFailed);
  const dispatch = useAppDispatch();
  const settingsLoadingFailed = useSelector(selectClientSettingsLoadingFailed);
  const settingsLoaded = useSelector(selectClientSettingsLoaded);
  const { mode } = useReportMode();

  useEffect(() => {
    if (!clientCode) {
      return;
    }
    dispatch(loadClientSettings());
  }, [clientCode, dispatch]);

  const shouldLoadMetaData = (mode === "build" || mode === "template") && !metaDataLoaded && clientCode !== undefined;
  useMetaDataInitialization(shouldLoadMetaData);

  if (settingsLoadingFailed || metaDataLoadingFailed) {
    return (
      <ImportantDataLoadingFailed
        title={commonLocale.report_data_loading_failed_title}
        disclaimer={commonLocale.report_data_loading_failed_text}
      />
    );
  }

  if ((!metaDataLoaded || !settingsLoaded) && (mode === "build" || mode === "template")) {
    return <InlineLoader text={metadataLocale.loading_label} />;
  }

  return children;
};
